import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { CurrencyEur, Bank, CreditCard, Coins, Plus, X, Receipt } from "@phosphor-icons/react";
import SearchBar, { textMatch } from "@/components/SearchBar";

const inputCls = "w-full bg-transparent border-b py-2 outline-none focus:border-[#C2A878] text-sm";

function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
      <div>
        <div className="overline mb-3">{eyebrow}</div>
        <h1 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-none">{title}</h1>
        {subtitle && <p className="mt-3 text-sm text-[#5C5A54] max-w-xl">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

function MoneyGroup({ obj, testId }) {
  const entries = Object.entries(obj || {});
  if (entries.length === 0) return <span className="text-[#8F8D84]" data-testid={testId}>—</span>;
  return (
    <div className="space-y-1" data-testid={testId}>
      {entries.map(([cur, v]) => (
        <div key={cur} className="font-mono text-sm">
          {cur === "TRY" ? "₺" : cur === "EUR" ? "€" : cur === "USD" ? "$" : cur} {Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}
        </div>
      ))}
    </div>
  );
}

/* ═════════════ FINANCE DASHBOARD ═════════════ */
export function FinanceDashboard() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [d, a] = await Promise.all([
          api.get("/finance/dashboard"),
          api.get("/finance/accounts"),
        ]);
        setData(d.data); setAccounts(a.data || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);
  const cardBase = { borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" };
  const balances = data?.balances_by_type || {};

  return (
    <div data-testid="finance-dashboard">
      <PageHeader eyebrow={t("nav.finance")} title={t("finance.dashboard")}
        subtitle={t("finance.subtitle")} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { key: "cash", label: t("finance.cash"), Icon: Coins },
          { key: "bank", label: t("finance.bank"), Icon: Bank },
          { key: "card", label: t("finance.card"), Icon: CreditCard },
          { key: "other", label: t("finance.other"), Icon: CurrencyEur },
        ].map(({ key, label, Icon }) => (
          <div key={key} className="border p-5" style={cardBase} data-testid={`finance-card-${key}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="overline">{label}</div>
              <Icon size={18} weight="light" className="text-[#8F8D84]" />
            </div>
            <MoneyGroup obj={balances[key]} testId={`finance-balance-${key}`} />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="border p-5" style={cardBase}>
          <div className="overline mb-2">{t("finance.collectionsMTD")}</div>
          <MoneyGroup obj={data?.collections_mtd} testId="finance-collections-mtd" />
        </div>
        <div className="border p-5" style={cardBase}>
          <div className="overline mb-2">{t("finance.expensesMTD")}</div>
          <MoneyGroup obj={data?.expenses_mtd} testId="finance-expenses-mtd" />
        </div>
        <div className="border p-5" style={cardBase}>
          <div className="overline mb-2">{t("finance.purchaseMTD")}</div>
          <MoneyGroup obj={data?.purchase_mtd} testId="finance-purchase-mtd" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="border p-5" style={cardBase}>
          <div className="overline mb-2">{t("finance.receivables")}</div>
          <div className="font-mono text-2xl" data-testid="finance-receivables">
            {(data?.receivables_total || 0).toLocaleString()}
          </div>
        </div>
        <div className="border p-5" style={cardBase}>
          <div className="overline mb-2">{t("finance.payables")}</div>
          <div className="font-mono text-2xl" data-testid="finance-payables">
            {(data?.payables_total || 0).toLocaleString()}
          </div>
        </div>
      </div>
      <div className="border" style={cardBase} data-testid="finance-accounts-card">
        <div className="p-4 border-b overline" style={{ borderColor: "var(--nylsu-border)" }}>
          {t("finance.accounts")}
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "#F4F3ED" }}>
              <th className="data-label px-4 py-3 text-left">{t("common.name")}</th>
              <th className="data-label px-4 py-3 text-left">{t("common.type")}</th>
              <th className="data-label px-4 py-3 text-left">{t("common.currency")}</th>
              <th className="data-label px-4 py-3 text-right">{t("finance.opening")}</th>
              <th className="data-label px-4 py-3 text-right">{t("finance.moneyIn")}</th>
              <th className="data-label px-4 py-3 text-right">{t("finance.moneyOut")}</th>
              <th className="data-label px-4 py-3 text-right">{t("finance.balance")}</th>
            </tr>
          </thead>
          <tbody>
            {accounts.length === 0 && <tr><td colSpan="7" className="text-center py-6 text-[#8F8D84]">{t("common.empty")}</td></tr>}
            {accounts.map((a) => (
              <tr key={a.id} className="border-t" style={{ borderColor: "var(--nylsu-border)" }} data-testid={`finance-account-${a.id}`}>
                <td className="px-4 py-2 font-serif">{a.name}</td>
                <td className="px-4 py-2 text-xs uppercase tracking-[0.1em]">{a.type}</td>
                <td className="px-4 py-2 font-mono text-xs">{a.currency}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">{Number(a.opening_balance || 0).toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-mono text-xs" style={{ color: "var(--nylsu-success)" }}>+{Number(a.money_in || 0).toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-mono text-xs" style={{ color: "var(--nylsu-danger)" }}>−{Number(a.money_out || 0).toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-mono">{Number(a.current_balance || 0).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═════════════ EXPENSES ═════════════ */
function NewExpenseModal({ onClose, onSaved, categories, accounts }) {
  const { t } = useI18n();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date: today, category: categories[0]?.category || "Other", subcategory: "",
    payee: "", description: "", amount: 0, currency: "TRY",
    payment_method: "", account_id: "", status: "unpaid",
    campaign: "", invoice_number: "", notes: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const subs = categories.find((c) => c.category === form.category)?.subcategories || [];
  const save = async () => {
    if (!form.description.trim() || Number(form.amount) <= 0) {
      toast.error(t("expense.needDescAmount")); return;
    }
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (!payload.account_id) delete payload.account_id;
      if (!payload.payment_method) delete payload.payment_method;
      const r = await api.post("/expenses", payload);
      toast.success(t("expense.createdToast", { number: r.data.expense_number }));
      onSaved(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="new-expense-modal">
      <div className="w-full max-w-2xl bg-white border shadow-xl max-h-[92vh] overflow-y-auto" style={{ borderColor: "var(--nylsu-border-strong)" }} onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: "var(--nylsu-border)" }}>
          <div className="overline">{t("expense.new")}</div>
          <button type="button" onClick={onClose} className="p-1 text-[#8F8D84] hover:text-[#0A0A0A]"><X size={14} /></button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-4">
          <div><label className="overline block mb-1">{t("common.date")}</label>
            <input type="date" className={inputCls} value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="expense-date" /></div>
          <div><label className="overline block mb-1">{t("common.amount")}</label>
            <input type="number" step="0.01" className={inputCls} value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="expense-amount" /></div>
          <div><label className="overline block mb-1">{t("common.category")}</label>
            <select className={inputCls} value={form.category} onChange={(e) => { set("category", e.target.value); set("subcategory", ""); }} data-testid="expense-category">
              {categories.map((c) => <option key={c.category}>{c.category}</option>)}
            </select></div>
          <div><label className="overline block mb-1">{t("expense.subcategory")}</label>
            <select className={inputCls} value={form.subcategory} onChange={(e) => set("subcategory", e.target.value)} data-testid="expense-subcategory">
              <option value="">—</option>
              {subs.map((s) => <option key={s}>{s}</option>)}
            </select></div>
          <div><label className="overline block mb-1">{t("expense.payee")}</label>
            <input className={inputCls} value={form.payee} onChange={(e) => set("payee", e.target.value)} data-testid="expense-payee" /></div>
          <div><label className="overline block mb-1">{t("common.currency")}</label>
            <select className={inputCls} value={form.currency} onChange={(e) => set("currency", e.target.value)}>
              {["TRY", "EUR", "USD", "GBP"].map((c) => <option key={c}>{c}</option>)}
            </select></div>
          <div className="col-span-2"><label className="overline block mb-1">{t("common.description")}</label>
            <input className={inputCls} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="expense-description" /></div>
          <div><label className="overline block mb-1">{t("expense.campaign")}</label>
            <input className={inputCls} value={form.campaign} onChange={(e) => set("campaign", e.target.value)} data-testid="expense-campaign" placeholder="e.g. Paris FW26" /></div>
          <div><label className="overline block mb-1">{t("expense.invoiceNumber")}</label>
            <input className={inputCls} value={form.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} /></div>
          <div><label className="overline block mb-1">{t("common.status")}</label>
            <select className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="expense-status">
              {["unpaid", "paid", "draft", "reimbursable"].map((s) => <option key={s}>{s}</option>)}
            </select></div>
          {form.status === "paid" && (
            <>
              <div><label className="overline block mb-1">{t("expense.method")}</label>
                <select className={inputCls} value={form.payment_method} onChange={(e) => set("payment_method", e.target.value)} data-testid="expense-method">
                  <option value="">—</option>
                  {["cash", "credit_card", "bank_transfer", "employee_paid", "owner_paid", "other"].map((m) => <option key={m}>{m}</option>)}
                </select></div>
              <div><label className="overline block mb-1">{t("expense.account")}</label>
                <select className={inputCls} value={form.account_id} onChange={(e) => set("account_id", e.target.value)} data-testid="expense-account">
                  <option value="">—</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
                </select></div>
            </>
          )}
        </div>
        <div className="p-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--nylsu-border)" }}>
          <button type="button" onClick={onClose} className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] border" style={{ borderColor: "var(--nylsu-border)" }}>{t("common.cancel")}</button>
          <button type="button" onClick={save} className="px-4 py-2 text-[10px] uppercase tracking-[0.15em] border" style={{ background: "var(--nylsu-gold)", borderColor: "var(--nylsu-gold)" }} data-testid="expense-save">{t("common.save")}</button>
        </div>
      </div>
    </div>
  );
}

export function ExpensesPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const canCreate = user?.role === "admin" || (user?.permissions || []).includes("expense:create");
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const load = async () => {
    try {
      const [e, c, a] = await Promise.all([
        api.get("/expenses"),
        api.get("/finance/expense-categories"),
        api.get("/finance/accounts").catch(() => ({ data: [] })),
      ]);
      setRows(e.data || []); setCategories(c.data || []); setAccounts(a.data || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);
  const filtered = useMemo(() => rows.filter((r) => {
    if (statusFilter && r.status !== statusFilter) return false;
    if (catFilter && r.category !== catFilter) return false;
    return textMatch(q, r.expense_number, r.description, r.payee, r.invoice_number, r.campaign);
  }), [rows, q, statusFilter, catFilter]);

  return (
    <div data-testid="expenses-page">
      <PageHeader eyebrow={t("nav.finance")} title={t("expense.title")} subtitle={t("expense.subtitle")}
        actions={canCreate && (
          <button type="button" onClick={() => setOpenNew(true)}
                  className="inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.2em]"
                  style={{ background: "#1A1A1A", color: "var(--nylsu-page)" }}
                  data-testid="new-expense-btn">
            <Plus size={14} weight="light" /> {t("expense.new")}
          </button>
        )} />
      <SearchBar testId="expenses-search" value={q} onChange={setQ}
        placeholder={t("expense.searchPlaceholder")}
        resultCount={`${filtered.length} / ${rows.length}`}
        filters={
          <>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84]">{t("common.status")}</label>
            <select className="text-xs bg-transparent border-b py-1 outline-none" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="expenses-status-filter">
              <option value="">{t("common.all")}</option>
              {["unpaid", "partially_paid", "paid", "draft", "cancelled"].map((s) => <option key={s}>{s}</option>)}
            </select>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84] ml-3">{t("common.category")}</label>
            <select className="text-xs bg-transparent border-b py-1 outline-none" value={catFilter} onChange={(e) => setCatFilter(e.target.value)} data-testid="expenses-category-filter">
              <option value="">{t("common.all")}</option>
              {categories.map((c) => <option key={c.category}>{c.category}</option>)}
            </select>
          </>
        } />
      <div className="border" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "#F4F3ED" }}>
              <th className="data-label px-4 py-3 text-left">{t("expense.number")}</th>
              <th className="data-label px-4 py-3 text-left">{t("common.date")}</th>
              <th className="data-label px-4 py-3 text-left">{t("common.category")}</th>
              <th className="data-label px-4 py-3 text-left">{t("expense.payee")}</th>
              <th className="data-label px-4 py-3 text-left">{t("common.description")}</th>
              <th className="data-label px-4 py-3 text-left">{t("expense.campaign")}</th>
              <th className="data-label px-4 py-3 text-right">{t("common.amount")}</th>
              <th className="data-label px-4 py-3 text-left">{t("common.status")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="8" className="text-center py-8 text-[#8F8D84]">{t("common.empty")}</td></tr>}
            {filtered.map((r) => (
              <tr key={r.id} className="border-t" style={{ borderColor: "var(--nylsu-border)" }} data-testid={`expense-row-${r.expense_number}`}>
                <td className="px-4 py-2 font-mono text-xs">{r.expense_number}</td>
                <td className="px-4 py-2 text-xs">{(r.date || "").slice(0, 10)}</td>
                <td className="px-4 py-2 text-xs">{r.category}{r.subcategory ? ` · ${r.subcategory}` : ""}</td>
                <td className="px-4 py-2 text-xs">{r.payee || "—"}</td>
                <td className="px-4 py-2 text-xs">{r.description}</td>
                <td className="px-4 py-2 text-xs text-[#8F8D84]">{r.campaign || "—"}</td>
                <td className="px-4 py-2 text-right font-mono">{r.currency} {Number(r.amount).toLocaleString()}</td>
                <td className="px-4 py-2">
                  <span className="uppercase text-[10px] tracking-[0.1em] px-2 py-1 border"
                        style={{
                          borderColor: r.status === "paid" ? "var(--nylsu-success)" : (r.status === "unpaid" ? "var(--nylsu-warning)" : "var(--nylsu-border)"),
                          color: r.status === "paid" ? "var(--nylsu-success)" : (r.status === "unpaid" ? "var(--nylsu-warning)" : "#8F8D84"),
                        }}>{r.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {openNew && (
        <NewExpenseModal categories={categories} accounts={accounts}
          onClose={() => setOpenNew(false)}
          onSaved={() => { setOpenNew(false); load(); }} />
      )}
    </div>
  );
}

/* ═════════════ PAYABLES ═════════════ */
export function PayablesPage() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/payables");
        setRows(r.data || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);
  return (
    <div data-testid="payables-page">
      <PageHeader eyebrow={t("nav.finance")} title={t("finance.payables")} subtitle={t("finance.payablesSubtitle")} />
      <div className="border" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "#F4F3ED" }}>
              <th className="data-label px-4 py-3 text-left">{t("finance.poNumber")}</th>
              <th className="data-label px-4 py-3 text-left">{t("common.supplier")}</th>
              <th className="data-label px-4 py-3 text-right">{t("finance.poTotal")}</th>
              <th className="data-label px-4 py-3 text-right">{t("finance.paid")}</th>
              <th className="data-label px-4 py-3 text-right">{t("finance.remaining")}</th>
              <th className="data-label px-4 py-3 text-left">{t("common.status")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-[#8F8D84]">{t("finance.noPayables")}</td></tr>}
            {rows.map((r) => (
              <tr key={r.po_id} className="border-t" style={{ borderColor: "var(--nylsu-border)" }} data-testid={`payable-row-${r.po_number}`}>
                <td className="px-4 py-2 font-mono text-xs">{r.po_number || "—"}</td>
                <td className="px-4 py-2">{r.supplier_name || "—"}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">{r.currency} {r.total.toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-mono text-xs" style={{ color: "var(--nylsu-success)" }}>{r.paid.toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-mono" style={{ color: r.remaining > 0 ? "var(--nylsu-warning)" : "var(--nylsu-success)" }}>{r.remaining.toLocaleString()}</td>
                <td className="px-4 py-2 text-xs uppercase tracking-[0.1em]">{r.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
